import React, { useState } from "react";
import { clsx } from "clsx";

import { twMerge } from "@/design-system/twMerge";

interface ToggleChoiceProps<Option, OptionValue> {
  className?: string;
  optionClassName?: string;
  triggerTracking?: string;
  disabled?: boolean;
  options: Option[];
  handleChange: (option: Option) => void;
  getOptionLabel: (option: Option) => React.ReactNode;
  getOptionValue: (option: Option) => OptionValue;
  getOptionAriaLabel?: (option: Option) => string;
  defaultValue?: Option;
  value?: Option;
  ariaLabel?: string;
}

export function ToggleChoice<Option, OptionValue>({
  className,
  optionClassName,
  disabled,
  options,
  handleChange,
  getOptionLabel,
  getOptionValue,
  getOptionAriaLabel,
  defaultValue,
  value,
  triggerTracking,
  ariaLabel,
}: ToggleChoiceProps<Option, OptionValue>) {
  const isControlled = value !== undefined;
  const [uncontrolledSelectedOptionValue, setUncontrolledSelectedOptionValue] =
    useState<OptionValue | "">(
      defaultValue !== undefined ? getOptionValue(defaultValue) : "",
    );
  const selectedOptionValue = isControlled
    ? getOptionValue(value)
    : uncontrolledSelectedOptionValue;
  const trackingId = triggerTracking?.trim();

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={twMerge(
        clsx(
          "border-borderSubtle bg-surface-default relative inline-flex items-center overflow-hidden rounded-md border p-0",
          className,
        ),
      )}
    >
      {options.map((option, index) => (
        <label
          key={index}
          className={twMerge(
            clsx(
              "text-textSubtle has-checked:text-textActionSelected has-checked:bg-surface-subtle border-borderSubtle cursor-pointer border-r px-3 py-1.5 font-medium transition last:border-r-0",
              disabled && "cursor-not-allowed opacity-50",
              optionClassName,
            ),
          )}
          onClick={(event) => {
            event.preventDefault();
            if (disabled) return;
            if (!isControlled) {
              setUncontrolledSelectedOptionValue(getOptionValue(option));
            }
            handleChange(option);
          }}
        >
          <input
            type="radio"
            className="sr-only"
            disabled={disabled}
            aria-label={getOptionAriaLabel?.(option)}
            checked={getOptionValue(option) === selectedOptionValue}
            onChange={() => {
              if (disabled) return;
              if (!isControlled) {
                setUncontrolledSelectedOptionValue(getOptionValue(option));
              }
              handleChange(option);
            }}
            data-track={
              trackingId
                ? `${trackingId}-${String(getOptionValue(option))}`
                : undefined
            }
          />
          <span>{getOptionLabel(option)}</span>
        </label>
      ))}
    </div>
  );
}
